import {
  FilterFn,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "../ui";
import NoData from "@/assets/noData.svg";
import { DataTableProps } from "./types";
import SortableTableHead from "./SortableTableHead";

const customGlobalFilterFn: FilterFn<any> = (row, columnId, filterValue) => {
  console.log("🚀 ~ customGlobalFilterFn ~ columnId:", columnId);
  if (!filterValue || typeof filterValue !== "string") return true;

  const searchTerms = filterValue
    .split(",")
    .map((term) => term.trim().toLowerCase())
    .filter(Boolean);

  if (searchTerms.length === 0) return true;

  // Retrieve values from all visible cells in the current row
  const rowValues = row
    .getAllCells()
    .map((cell) => String(cell.getValue() ?? "").toLowerCase());

  // Matches if ANY search term exists in ANY cell value
  return searchTerms.some((term) =>
    rowValues.some((value) => value.includes(term))
  );
};

const DataTable: FC<DataTableProps> = ({
  data,
  columns,
  globalFilter,
  setGlobalFilter,
  rowSelection,
  setRowSelection,
  setPageNumber,
  setPageSize,
  serverSidePagination,
  totalPages,
  pageNumber,
  pageSize,
  onRowClick,
  totalRecords,
  className,
  hideFooter,
  paginationArr,
  tableWrapperClassName,
  headerClass,
  lastRow,
  sorting: sortingProp,
  onSortingChange,
  enableSorting = true,
  manualSorting,
}) => {
  const [pagination, setPagination] = useState({
    pageIndex: pageNumber || 0, // Start from the first page
    pageSize: pageSize || 10, // Default Rows per Page
  });
  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>([]);
  const sorting = sortingProp ?? uncontrolledSorting;

  const _totalRecords = totalRecords ? totalRecords : data.length;
  
  const table = useReactTable({
    data,
    columns,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    manualPagination: serverSidePagination,
    manualSorting: manualSorting ?? !!serverSidePagination,
    enableSorting,
    enableSortingRemoval: true,
    globalFilterFn: customGlobalFilterFn,
    state: {
      globalFilter,
      pagination, // Use the pagination state
      rowSelection,
      sorting,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      if (sortingProp === undefined) {
        setUncontrolledSorting(next);
      }
      onSortingChange?.(next);
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination, // Correctly update pagination state
    ...(serverSidePagination && {
      pageCount: totalPages,
    }),
  });

  useEffect(() => {
    setPageNumber && setPageNumber(pagination.pageIndex);
    setPageSize && setPageSize(pagination.pageSize);
  }, [pagination]);

  return (
    <div
      className={cn(
        "space-y-4 rounded-lg border border-gray-200 shadow-md overflow-hidden bg-white overflow-x-auto w-full",
        className
      )}
    >
      {/* Table Wrapper with Horizontal Scroll for Small Screens */}
      <div className={cn("lg:w-full px-4", tableWrapperClassName)}>
        <Table>
          <TableHeader>
            {table?.getHeaderGroups()?.map((headerGroup) => (
              <TableRow key={headerGroup.id} className={cn("border-g-200", headerClass?.row)}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHead
                    key={header.id}
                    header={header}
                    className={cn(
                      (header.column.columnDef.meta as any)?.align === "right"
                        ? "text-right"
                        : "text-left",
                      "h-[50px] text-primary font-[600]",
                      headerClass?.cell
                    )}
                    style={{ width: header.column.getSize() ?? "auto" }}
                  />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              <>
                {table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "border-g-200",
                      index % 2 === 1 ? "bg-g-100" : "",
                      onRowClick && "cursor-pointer"
                    )}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          (cell.column.columnDef.meta as any)?.align === "right"
                            ? "text-right"
                            : "text-left",
                          "h-[45px] text-g-500"
                        )}
                        style={{ width: cell.column.getSize() ?? "auto" }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {lastRow ? lastRow : <></>}
              </>
            ) : (
              <TableRow className="w-full">
                <TableCell colSpan={columns.length} className="py-10">
                  <div className="flex flex-col items-center justify-center">
                    <img src={NoData} className="w-[200px] h-auto" />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {!hideFooter && _totalRecords > 0 && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-end p-4 gap-4 text-sm text-g-400 text-[15px]"
          )}
        >
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              className="rounded px-1 py-1"
              value={pagination.pageSize}
              onChange={(e) =>
                setPagination((prev) => ({
                  ...prev,
                  pageSize: Number(e.target.value),
                  pageIndex: 0, // Reset to first page when changing page size
                }))
              }
            >
              {[...(paginationArr ? paginationArr : [5, 10, 20, 50])].map(
                (size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                )
              )}
            </select>
          </div>
          <div>
            <span>
              {pagination.pageIndex * pagination.pageSize + 1} -{" "}
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                _totalRecords
              )}{" "}
              of {_totalRecords}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-2 py-1 shadow-none !bg-transparent"
            >
              <ChevronLeft className="w-6 h-6 text-g-400" />
            </Button>
            <Button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-2 py-1 shadow-none !bg-transparent"
            >
              <ChevronRight className="w-6 h-6 text-g-400" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
